import { useRef, useState } from 'react';
import { Button, Progress } from 'antd';

import FieldHelp, { elementHelpText } from './FieldHelp';
import { ARROW_CLOCKWISE, CLOSE, FILE_PDF, IMAGE, PLUS } from '../icons';
import { formatBytes } from '../../utils/formatters';
import { typesInWords, uploadAccept } from '../../utils/intakeLinks';

/**
 * The visitor's own preview of a picked file: the photo itself, from their
 * device (an object URL — nothing comes back from keepr), or an icon — a
 * PDF, or a photo the browser cannot draw (a HEIC outside Safari).
 */
function Thumb({ entry }) {
	const [broken, setBroken] = useState(false);
	if (entry.kind === 'photo' && entry.previewUrl && !broken) {
		return <img src={entry.previewUrl} alt="" draggable={false} onError={() => setBroken(true)} />;
	}
	return entry.kind === 'pdf' ? <FILE_PDF /> : <IMAGE />;
}

/**
 * A `file` element on a PUBLIC form (KPR-388) — what a visitor with no
 * account uses to add photos to an intake link's submission. Not the item
 * form's FileUploadField: that one uploads to a signed-in person's held
 * files, and on this page there is no session (an upload here goes to the
 * link, under the form's submission token; services/shareLinks
 * uploadSharedFile).
 *
 * This component only SHOWS and PICKS. The page that owns it
 * (pages/SharedFormPage) holds the picked files, sends them, retries them,
 * and turns the finished ones into the element's value — because a token
 * refresh re-sends every file on the form, not one field's.
 *
 *   element  the public projection: { name, label, options: { required?,
 *            allowMultiple? }, upload: { types, maxFileMb } }
 *   entries  [{ uid, name, size, kind: 'photo'|'pdf', previewUrl,
 *            state: 'uploading'|'done'|'error', percent, error }] — the
 *            visitor's own files, previewed from their own device (nothing
 *            is fetched back from keepr)
 *   onPick(files) / onRemove(uid) / onRetry(uid)
 *
 * Add photo is a real button (keyboard and screen reader) that opens the
 * hidden picker; never `capture`, so a phone offers the camera AND the
 * library. A single-file element shows Replace once it holds one.
 */
export default function PublicFileField({
	element,
	entries = [],
	onPick,
	onRemove,
	onRetry,
	disabled = false,
	errorMessage = null,
	full = false,
}) {
	const inputRef = useRef(null);
	const name = element?.name || 'file';
	const label = element?.label?.singular || name;
	const required = element?.options?.required === true;
	const multiple = element?.options?.allowMultiple === true;
	const upload = element?.upload || {};
	const types = Array.isArray(upload.types) && upload.types.length ? upload.types : ['photo'];
	const photosOnly = !types.includes('pdf');
	const buttonId = `public-file-${name}`;
	const labelId = `public-file-label-${name}`;
	const hintId = `public-file-hint-${name}`;
	const errorId = `public-file-error-${name}`;
	const holdsOne = !multiple && entries.length > 0;
	const addWords = holdsOne
		? (photosOnly ? 'Replace photo' : 'Replace file')
		: (photosOnly ? (multiple ? 'Add photos' : 'Add photo') : (multiple ? 'Add files' : 'Add a file'));

	const pick = (event) => {
		const files = Array.from(event.target.files || []);
		// The same file picked twice in a row must still fire a change.
		event.target.value = '';
		if (files.length) onPick?.(files);
	};

	return (
		<div id={`element-field-${name}`} className={`element-field public-file${errorMessage ? ' element-field--error' : ''}`} data-testid="public-file">
			{/* A group named by the field's label: the button keeps its own name
			    ("Add photos"), which is what a screen reader should announce for
			    it; a <label for> would rename it to the field's. */}
			<div className="mb-2" role="group" aria-labelledby={labelId}>
				<FieldHelp text={elementHelpText(element)} label={label}>
					<label className="d-block" id={labelId}>
						<span>
							{label}
							{required ? <span className="required-indicator" aria-hidden="true">*</span> : null}
						</span>
					</label>
				</FieldHelp>

				{entries.length ? (
					<ul className="public-file__list" aria-label={`${label}: files added`}>
						{entries.map(entry => (
							<li key={entry.uid} className={`public-file__item public-file__item--${entry.state}`} data-testid="public-file-item" data-state={entry.state}>
								<span className="public-file__thumb" aria-hidden="true">
									<Thumb entry={entry} />
								</span>
								<span className="public-file__body">
									<span className="public-file__name" title={entry.name}>{entry.name}</span>
									{entry.state === 'uploading' ? (
										<Progress percent={entry.percent || 0} size="small" showInfo={false} aria-label={`Sending ${entry.name}`} />
									) : null}
									{entry.state === 'done' ? (
										<span className="public-file__meta">{Number.isFinite(entry.size) ? formatBytes(entry.size) : ''} · Ready</span>
									) : null}
									{entry.state === 'error' ? (
										<span className="public-file__error" role="alert">{entry.error || 'This file was not sent.'}</span>
									) : null}
								</span>
								<span className="public-file__actions">
									{entry.state === 'error' && entry.retryable !== false ? (
										<Button
											size="small"
											icon={<ARROW_CLOCKWISE />}
											onClick={() => onRetry?.(entry.uid)}
											disabled={disabled}
											aria-label={`Try sending ${entry.name} again`}
										/>
									) : null}
									<Button
										size="small"
										icon={<CLOSE />}
										onClick={() => onRemove?.(entry.uid)}
										disabled={disabled}
										aria-label={`Remove ${entry.name}`}
									/>
								</span>
							</li>
						))}
					</ul>
				) : null}

				<input
					ref={inputRef}
					type="file"
					className="public-file__input"
					accept={uploadAccept(types)}
					multiple={multiple}
					onChange={pick}
					tabIndex={-1}
					aria-hidden="true"
					data-testid={`public-file-input-${name}`}
				/>
				{full && !holdsOne ? null : (
					<Button
						id={buttonId}
						icon={<PLUS />}
						className="public-file__add"
						onClick={() => inputRef.current?.click()}
						disabled={disabled}
						aria-describedby={`${hintId}${errorMessage ? ` ${errorId}` : ''}`}
					>
						{addWords}
					</Button>
				)}
				<p id={hintId} className="public-file__hint">
					{`Up to ${upload.maxFileMb || 10} MB a file, ${typesInWords(types)}.`}
					{types.includes('photo') ? ' Photos are sent without their location or camera details.' : ''}
				</p>
				{errorMessage ? <div id={errorId} className="element-field__error" role="alert">{errorMessage}</div> : null}
			</div>
		</div>
	);
}
